// src/components/Home/RedacaoEntryCard.js

import React, { useEffect, useRef, useState } from 'react';
import {
    View,
    StyleSheet,
    TouchableOpacity,
    Animated,
    Dimensions,
    Easing, Platform,
} from 'react-native';
import { Text } from '../ui/AppText';
import { LinearGradient } from 'expo-linear-gradient';
import Ionicons from '@expo/vector-icons/Ionicons';
import * as Haptics from 'expo-haptics';
import { useNavigation, useIsFocused } from '@react-navigation/native';

import { getEstatisticasHistorico } from '../../services/storage/redacaoStorageService';

const { width: SCREEN_WIDTH } = Dimensions.get('window');

// ============================================
// CORES
// ============================================
const COLORS = {
    primary: '#5B8FF9',
    primaryLight: '#7AA6FF',
    accent: '#F59E0B',
    accentLight: '#FBBF24',
    background: '#12121A',
    textPrimary: '#FFFFFF',
    textSecondary: 'rgba(255,255,255,0.7)',
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
const RedacaoEntryCard = ({ style }) => {
// Animações decorativas PAUSAM quando a tela perde o foco — não reverter.
//
// Medido no aparelho (2026-08-29): a Home NÃO desmonta ao navegar (o stack a
// mantém montada), então o cleanup do useEffect nunca rodava e estes loops
// seguiam a 60fps enquanto outra tela abria. Isso MATAVA DE FOME o render
// suspenso da tela de destino: enquanto o Suspense está pendente o React
// renderiza a tela nova fora da tela, e cada quadro de animação invalidava esse
// trabalho antes de ele terminar. Resultado: Perfil e Comunidade ficavam presas
// no spinner até o usuário desistir. Pré-carregar o chunk NÃO resolvia — medido:
// módulo pronto em 1,1s e o Suspense pendurando mesmo assim.
// Pausar no blur conserta a classe inteira e ainda poupa bateria.
    const isFocused = useIsFocused();

    const navigation = useNavigation();

    // Estados
    const [stats, setStats] = useState({ totalRedacoes: 0, mediaNotas: 0 });

    // Animações
    const pulseAnim = useRef(new Animated.Value(1)).current;
    const glowAnim = useRef(new Animated.Value(0)).current;
    const shimmerAnim = useRef(new Animated.Value(0)).current;
    const badgePulse = useRef(new Animated.Value(1)).current;
    const arrowAnim = useRef(new Animated.Value(0)).current;
    const scaleAnim = useRef(new Animated.Value(1)).current;

    // ============================================
    // EFEITOS
    // ============================================
    useEffect(() => {
        // Carregar estatísticas
        loadStats();

        // Animação de pulse do ícone
        const pulseLoop = Animated.loop(
            Animated.sequence([
                Animated.timing(pulseAnim, {
                    toValue: 1.07,
                    duration: 1500,
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
                Animated.timing(pulseAnim, {
                    toValue: 1,
                    duration: 1500,
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
            ])
        );

        // Animação de glow
        const glowLoop = Animated.loop(
            Animated.sequence([
                Animated.timing(glowAnim, {
                    toValue: 1,
                    duration: 2000,
                    useNativeDriver: false,
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
                Animated.timing(glowAnim, {
                    toValue: 0,
                    duration: 2000,
                    useNativeDriver: false,
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
            ])
        );

        // Animação de shimmer
        const shimmerLoop = Animated.loop(
            Animated.timing(shimmerAnim, {
                toValue: 1,
                duration: 3000,
                easing: Easing.linear,
                useNativeDriver: Platform.OS !== 'web',
                isInteraction: false, // loop infinito NAO e interacao do usuario
            })
        );

        // Animação do badge
        const badgeLoop = Animated.loop(
            Animated.sequence([
                Animated.timing(badgePulse, {
                    toValue: 1.15,
                    duration: 800,
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
                Animated.timing(badgePulse, {
                    toValue: 1,
                    duration: 800,
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
            ])
        );

        // Animação da seta
        const arrowLoop = Animated.loop(
            Animated.sequence([
                Animated.timing(arrowAnim, {
                    toValue: 1,
                    duration: 600,
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
                Animated.timing(arrowAnim, {
                    toValue: 0,
                    duration: 600,
                    useNativeDriver: Platform.OS !== 'web',
                    isInteraction: false, // loop infinito NAO e interacao do usuario
                }),
            ])
        );

        const loops = [pulseLoop, glowLoop, shimmerLoop, badgeLoop, arrowLoop];
        if (isFocused) loops.forEach((l) => l.start());

        return () => loops.forEach((l) => l.stop());
    }, [isFocused]);

    const loadStats = async () => {
        try {
            const estatisticas = await getEstatisticasHistorico();
            if (estatisticas) {
                setStats({
                    totalRedacoes: estatisticas.totalRedacoes || 0,
                    mediaNotas: estatisticas.mediaNotas || 0,
                });
            }
        } catch (error) {
            // Silencioso - stats são opcionais
        }
    };

    // ============================================
    // HANDLERS
    // ============================================
    const handlePressIn = () => {
        Animated.spring(scaleAnim, {
            toValue: 0.97,
            useNativeDriver: true,
        }).start();
    };

    const handlePressOut = () => {
        Animated.spring(scaleAnim, {
            toValue: 1,
            friction: 3,
            useNativeDriver: true,
        }).start();
    };

    const handlePress = () => {
        Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium);
        navigation.navigate('Redacao');
    };

    // ============================================
    // INTERPOLAÇÕES
    // ============================================
    const shimmerTranslate = shimmerAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [-SCREEN_WIDTH, SCREEN_WIDTH],
    });

    const glowOpacity = glowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0.3, 0.6],
    });

    const arrowTranslate = arrowAnim.interpolate({
        inputRange: [0, 1],
        outputRange: [0, 5],
    });

    // ============================================
    // RENDER
    // ============================================
    return (
        <Animated.View
            style={[
                styles.container,
                { transform: [{ scale: scaleAnim }] },
                style,
            ]}
        >
            <TouchableOpacity
                activeOpacity={1}
                onPressIn={handlePressIn}
                onPressOut={handlePressOut}
                onPress={handlePress}
            >
                <LinearGradient
                    colors={['#1E1E2E', '#12121A']}
                    style={styles.card}
                >
                    {/* Shimmer Effect */}
                    <Animated.View
                        style={[
                            styles.shimmer,
                            { transform: [{ translateX: shimmerTranslate }] }
                        ]}
                    >
                        <LinearGradient
                            colors={['transparent', 'rgba(255,255,255,0.05)', 'transparent']}
                            start={{ x: 0, y: 0 }}
                            end={{ x: 1, y: 0 }}
                            style={StyleSheet.absoluteFill}
                        />
                    </Animated.View>

                    {/* Badge NOVO */}
                    <Animated.View
                        style={[
                            styles.badge,
                            { transform: [{ scale: badgePulse }] }
                        ]}
                    >
                        <LinearGradient
                            colors={[COLORS.accent, COLORS.accentLight]}
                            style={styles.badgeGradient}
                        >
                            <Ionicons name="sparkles" size={10} color="#FFF" />
                            <Text style={styles.badgeText}>NOVO</Text>
                        </LinearGradient>
                    </Animated.View>

                    {/* Layout Principal - Ícone à esquerda, Textos à direita */}
                    <View style={styles.mainContent}>
                        {/* Ícone Pulsante */}
                        <View style={styles.iconSection}>
                            <Animated.View
                                style={[
                                    styles.iconContainer,
                                    { transform: [{ scale: pulseAnim }] }
                                ]}
                            >
                                <LinearGradient
                                    colors={[COLORS.primary, COLORS.primaryLight]}
                                    style={styles.iconGradient}
                                >
                                    <Ionicons name="document-text" size={32} color="#FFF" />
                                </LinearGradient>

                                {/* Glow do ícone */}
                                <Animated.View
                                    style={[
                                        styles.iconGlow,
                                        { opacity: glowOpacity }
                                    ]}
                                />
                            </Animated.View>
                        </View>

                        {/* Textos e Botão */}
                        <View style={styles.rightContent}>
                            {/* Título */}
                            <Text style={styles.title}>Corretor de Redação</Text>

                            {/* Linha com texto e botão */}
                            <View style={styles.textButtonRow}>
                                <Text style={styles.subtitle}>
                                    Receba nota, erros destacados{'\n'}e feedback completo por IA.
                                </Text>

                                {/* Botão CTA */}
                                <LinearGradient
                                    colors={[COLORS.primary, '#4A7AE5']}
                                    start={{ x: 0, y: 0 }}
                                    end={{ x: 1, y: 0 }}
                                    style={styles.ctaButton}
                                >
                                    <Text style={styles.ctaText}>Começar</Text>
                                    <Animated.View
                                        style={{ transform: [{ translateX: arrowTranslate }] }}
                                    >
                                        <Ionicons name="arrow-forward" size={16} color="#FFF" />
                                    </Animated.View>
                                </LinearGradient>
                            </View>
                        </View>
                    </View>

                    {/* Decoração de cantos */}
                    <View style={styles.cornerTL} />
                    <View style={styles.cornerBR} />
                </LinearGradient>
            </TouchableOpacity>
        </Animated.View>
    );
};

// ============================================
// ESTILOS
// ============================================
const styles = StyleSheet.create({
    container: {
        marginVertical: 12,
    },
    card: {
        borderRadius: 20,
        padding: 16,
        overflow: 'hidden',
        borderWidth: 1,
        borderColor: 'rgba(91, 143, 249, 0.3)',
    },
    shimmer: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        width: 100,
    },

    // Badge
    badge: {
        position: 'absolute',
        top: 12,
        right: 12,
        zIndex: 10,
    },
    badgeGradient: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        paddingHorizontal: 10,
        paddingVertical: 5,
        borderRadius: 12,
    },
    badgeText: {
        color: '#FFF',
        fontSize: 10,
        fontWeight: '800',
        letterSpacing: 1,
    },

    // Layout Principal
    mainContent: {
        flexDirection: 'row',
        alignItems: 'center',
    },

    // Ícone
    iconSection: {
        marginRight: 16,
    },
    iconContainer: {
        position: 'relative',
    },
    iconGradient: {
        width: 64,
        height: 64,
        borderRadius: 20,
        alignItems: 'center',
        justifyContent: 'center',
        shadowColor: COLORS.primary,
        shadowOffset: { width: 0, height: 8 },
        shadowOpacity: 0.5,
        shadowRadius: 16,
        elevation: 15,
    },
    iconGlow: {
        position: 'absolute',
        top: -10,
        left: -10,
        right: -10,
        bottom: -10,
        borderRadius: 32,
        backgroundColor: COLORS.primary,
    },

    // Conteúdo à direita
    rightContent: {
        flex: 1,
        paddingTop: 2,
    },
    title: {
        fontSize: 18,
        fontWeight: '800',
        color: COLORS.textPrimary,
        marginBottom: 6,
    },
    textButtonRow: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
    },
    subtitle: {
        flex: 1,
        fontSize: 12,
        color: COLORS.textSecondary,
        lineHeight: 17,
        marginRight: 12,
    },

    // CTA
    ctaButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        paddingVertical: 8,
        paddingHorizontal: 12,
        borderRadius: 10,
    },
    ctaText: {
        color: '#FFF',
        fontSize: 12,
        fontWeight: '700',
    },

    // Decoração
    cornerTL: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: 40,
        height: 40,
        borderTopLeftRadius: 20,
        borderLeftWidth: 2,
        borderTopWidth: 2,
        borderColor: 'rgba(91, 143, 249, 0.5)',
    },
    cornerBR: {
        position: 'absolute',
        bottom: 0,
        right: 0,
        width: 40,
        height: 40,
        borderBottomRightRadius: 20,
        borderRightWidth: 2,
        borderBottomWidth: 2,
        borderColor: 'rgba(245, 158, 11, 0.5)',
    },
});

export default RedacaoEntryCard;
